// frontend/src/pages/org/onboarding-page.tsx
// Read DESIGN.md before modifying this file. Blue info Alert pattern for the
// downgraded (SaaS Mode disabled) notice; Title Case button labels;
// Enabled/Revoked state words (never Active/Inactive).
//
// Onboarding links are RE-DISPLAYABLE (never shown-once): every row carries its
// standing connect URL with a copy button. url is null only on legacy rows
// minted before tokens were stored, where Regenerate mints a copyable one.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Combobox } from '@base-ui/react/combobox';
import { ChevronsUpDown, Loader2, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFetch } from '@/hooks/use-fetch';
import { useOrg } from '@/components/org-layout';
import { parseApiError } from '@/lib/api-error';
import { RoleGate } from '@/components/auth/role-gate';
import { CopyButton } from '@/components/onboarding/copy-button';
import { PersistentLinksCard } from '@/components/onboarding/persistent-links-card';
import { CreateLinkDialog } from '@/components/onboarding/create-link-dialog';
import { PageContainer } from '@/components/page-container';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';

/** Row of GET /org/onboarding-links. */
export interface OnboardingLinkRow {
  publicId: string;
  label: string;
  channelType: 'whatsapp' | 'instagram' | 'facebook';
  status: 'active' | 'revoked';
  createdAt: string;
  externalId: string | null;
  /** Standing connect URL; null only on legacy rows (Regenerate to mint one). */
  url: string | null;
  verifyToken: string;
  // Editable fields — used to prefill the Edit dialog.
  destinationWebhookUrl: string | null;
  destinationVerifyTokenOverride: string | null;
  successRedirectUrl: string | null;
  failureRedirectUrl: string | null;
  /** The customer this link attaches channels to; null = link creates one. */
  targetWorkspace: { publicId: string; name: string; externalId: string | null } | null;
}

/** Map a row to the shape the Edit dialog prefills from. */
function toEditable(link: OnboardingLinkRow) {
  return {
    publicId: link.publicId,
    channelType: link.channelType,
    label: link.label,
    externalId: link.externalId,
    destinationWebhookUrl: link.destinationWebhookUrl,
    destinationVerifyTokenOverride: link.destinationVerifyTokenOverride,
    successRedirectUrl: link.successRedirectUrl,
    failureRedirectUrl: link.failureRedirectUrl,
  };
}

const CHANNEL_LABEL: Record<OnboardingLinkRow['channelType'], string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

const ALL_CUSTOMERS = 'all';

/** A customer option for the fuzzy filter combobox. */
interface CustomerOption {
  publicId: string;
  name: string;
}
const ALL_CUSTOMERS_OPTION: CustomerOption = { publicId: ALL_CUSTOMERS, name: 'All Customers' };

/** Member-facing fallback when the user is not an org admin. */
function OnboardingMemberFallback() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Admin access required</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Onboarding is managed by your organization admins.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Admin-only content of the Onboarding page. Rendered inside RoleGate so it
 * never mounts for members or while the session is still loading.
 *
 * Lists links (each re-displayable with a copy button), mints new ones via the
 * shared CreateLinkDialog, exposes Revoke + Regenerate per active link, and
 * surfaces the persistent links card. The list is searchable (label, external
 * id, customer, id) and filterable by customer.
 */
function OnboardingContent() {
  const { orgId } = useParams<{ orgId: string }>();
  const fetchWithAuth = useFetch();
  const { refreshOrg } = useOrg();

  const [links, setLinks] = useState<OnboardingLinkRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLink, setEditLink] = useState<OnboardingLinkRow | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<OnboardingLinkRow | null>(null);
  const [busyPublicId, setBusyPublicId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState(ALL_CUSTOMERS);

  // The list shows only active links: revoked links are kept server-side for
  // history but surfaced here as "removed". This mirrors the sidebar's
  // hasActiveOnboardingLinks self-removal semantics (R4-F4/R5-F4).
  const activeLinks = links.filter((l) => l.status === 'active');

  // Customer filter options: "All Customers" plus the distinct target customers
  // among active links. Held client-side so the combobox fuzzy-filters in memory.
  const customerItems = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of activeLinks) {
      if (l.targetWorkspace) seen.set(l.targetWorkspace.publicId, l.targetWorkspace.name);
    }
    return [
      ALL_CUSTOMERS_OPTION,
      ...[...seen].map(([publicId, name]) => ({ publicId, name })),
    ];
  }, [activeLinks]);

  const selectedCustomer =
    customerItems.find((c) => c.publicId === customerFilter) ?? ALL_CUSTOMERS_OPTION;

  const visibleLinks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return activeLinks.filter((l) => {
      if (customerFilter !== ALL_CUSTOMERS && l.targetWorkspace?.publicId !== customerFilter) {
        return false;
      }
      if (!q) return true;
      return [
        l.label,
        l.publicId,
        l.externalId ?? '',
        l.targetWorkspace?.name ?? '',
        l.targetWorkspace?.publicId ?? '',
        l.targetWorkspace?.externalId ?? '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [activeLinks, customerFilter, search]);

  const loadLinks = useCallback(async () => {
    const res = await fetchWithAuth('/org/onboarding-links');
    if (res.ok) {
      const data = (await res.json()) as { links: OnboardingLinkRow[] };
      setLinks(data.links);
    } else {
      const body = await res.json().catch(() => null);
      toast.error(parseApiError(res.status, body, 'Failed to load onboarding links').userMessage);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  async function handleRevoke(link: OnboardingLinkRow) {
    setBusyPublicId(link.publicId);
    try {
      const res = await fetchWithAuth(`/org/onboarding-links/${link.publicId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Link revoked');
        await loadLinks();
        await refreshOrg();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to revoke link').userMessage);
      }
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setBusyPublicId(null);
      setRevokeTarget(null);
    }
  }

  async function handleRegenerate(link: OnboardingLinkRow) {
    setBusyPublicId(link.publicId);
    try {
      const res = await fetchWithAuth(`/org/onboarding-links/${link.publicId}/regenerate`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Link regenerated');
        await loadLinks();
        await refreshOrg();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to regenerate link').userMessage);
      }
    } catch {
      toast.error('Failed to regenerate link');
    } finally {
      setBusyPublicId(null);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Onboarding</h1>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="size-3.5" />
          New Link
        </Button>
      </div>

      <CreateLinkDialog
        open={dialogOpen || editLink !== null}
        editLink={editLink ? toEditable(editLink) : null}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditLink(null);
          }
        }}
        onSaved={async () => {
          await loadLinks();
          await refreshOrg();
        }}
      />

      {orgId && <PersistentLinksCard orgId={orgId} />}

      {activeLinks.length > 0 && (
        <Card className="min-h-0 flex-1 !overflow-visible">
          <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search links..."
                aria-label="Search links"
                className="max-w-xs"
              />
              {/* base-ui Combobox with the default contains-matcher: the full
                  customer list is client-side, so typing fuzzy-filters in memory.
                  Selecting "All Customers" clears the filter. */}
              <Combobox.Root
                items={customerItems}
                value={selectedCustomer}
                onValueChange={(item: CustomerOption | null) =>
                  setCustomerFilter(item?.publicId ?? ALL_CUSTOMERS)
                }
                itemToStringLabel={(item: CustomerOption | null) =>
                  item && item.publicId !== ALL_CUSTOMERS ? item.name : ''
                }
                isItemEqualToValue={(a: CustomerOption, b: CustomerOption) =>
                  a?.publicId === b?.publicId
                }
              >
                <div className="relative w-56">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Combobox.Input
                    aria-label="Filter by customer"
                    placeholder="All Customers"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent py-1 pr-8 pl-8 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  />
                  <Combobox.Trigger
                    aria-label="Open customer list"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground outline-none hover:text-foreground"
                  >
                    <ChevronsUpDown className="size-4" />
                  </Combobox.Trigger>
                </div>
                <Combobox.Portal>
                  <Combobox.Positioner positionMethod="fixed" align="start" side="bottom" sideOffset={4} className="z-50">
                    <Combobox.Popup className="w-(--anchor-width) max-h-60 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none">
                      <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                        No customers match your search
                      </Combobox.Empty>
                      <Combobox.List>
                        {(item: CustomerOption) => (
                          <Combobox.Item
                            key={item.publicId}
                            value={item}
                            className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                          >
                            <span className="truncate">{item.name}</span>
                          </Combobox.Item>
                        )}
                      </Combobox.List>
                    </Combobox.Popup>
                  </Combobox.Positioner>
                </Combobox.Portal>
              </Combobox.Root>
            </div>
            <div className="scrollable-table min-h-0 flex-1 [&_[data-slot=table-container]]:h-full">
              <Table className="table-fixed max-md:min-w-[44rem]">
                <colgroup>
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleLinks.map((link) => (
                    <TableRow key={link.publicId}>
                      <TableCell className="truncate font-medium">{link.label}</TableCell>
                      <TableCell>{CHANNEL_LABEL[link.channelType]}</TableCell>
                      <TableCell className="truncate">
                        {link.targetWorkspace?.name ?? (
                          <span className="text-muted-foreground">New customer</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-1">
                          <span
                            className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground"
                            title={link.url ?? ''}
                          >
                            {link.url ? midEllipsisUrl(link.url) : null}
                          </span>
                          <CopyButton value={link.url ?? ''} label={`Connect link for ${link.label}`} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            aria-label={`Edit ${link.label}`}
                            onClick={() => setEditLink(link)}
                          >
                            <Pencil className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            disabled={busyPublicId === link.publicId}
                            aria-label={`Regenerate ${link.label}`}
                            onClick={() => void handleRegenerate(link)}
                          >
                            <RefreshCw className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            disabled={busyPublicId === link.publicId}
                            aria-label={`Revoke ${link.label}`}
                            onClick={() => setRevokeTarget(link)}
                          >
                            <Trash2 className="size-3.5 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleLinks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No links match your search.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!busyPublicId && !open) setRevokeTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this onboarding link?</AlertDialogTitle>
            <AlertDialogDescription>
              The link stops working immediately and any customer who opens it
              sees an inactive-link page. This cannot be undone. You can create a
              new link at any time while SaaS Mode is enabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Link</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={busyPublicId !== null}
              onClick={() => {
                if (revokeTarget) void handleRevoke(revokeTarget);
              }}
            >
              {busyPublicId !== null && <Loader2 className="size-3.5 animate-spin" />}
              Revoke Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Onboarding page — admin-only route. Non-admins and the loading state both
 * render the member fallback (RoleGate handles it). The sidebar item is also
 * hidden for members (OrgSidebar), but the RoleGate is the hard deep-link gate.
 */
export function OnboardingPage() {
  return (
    <PageContainer variant="fill" className="gap-4">
      <RoleGate
        role="admin"
        fallback={
          <>
            <h1 className="text-xl font-semibold">Onboarding</h1>
            <OnboardingMemberFallback />
          </>
        }
      >
        <OnboardingContent />
      </RoleGate>
    </PageContainer>
  );
}
